import { useState } from 'react';
import {
  CurrencyFormat,
  Icon,
  NoFound,
  Search,
  Spinner,
  Table,
  TableRow,
} from '@/components/generic';
import { cn } from '@/lib/utils';
import { SelectOption } from '@/types/select';
import { copyToClipboard, getCurrencySymbol, shortenAddress } from '@/utils';
import { useGetUserCardTxs } from '@package/smart-wallet/hooks';
import { useTranslation } from 'react-i18next';

import { useCardContext } from '../context/useCardContext';

// Transaction types with color classifications for Card
const CARD_TRANSACTION_TYPES = {
  CREATE: { label: 'Create Card', color: 'label-blue', priority: 'high' },
  DEPOSIT: { label: 'Deposit', color: 'label-green', priority: 'high' },
  CANCEL: { label: 'Cancel Card', color: 'label-red', priority: 'high' },
  FREEZE: { label: 'Freeze Card', color: 'label-orange', priority: 'medium' },
  UNFREEZE: {
    label: 'Unfreeze Card',
    color: 'label-green',
    priority: 'medium',
  },
  WITHDRAW: { label: 'Withdraw', color: 'label-purple', priority: 'high' },
} as const;

const STATUS_COLORS = {
  PENDING: 'label-yellow',
  SUCCESS: 'label-green',
  COMPLETED: 'label-green',
  FAILED: 'label-red',
  CANCELLED: 'label-gray',
  ACTIVE: 'label-green',
  INACTIVE: 'label-gray',
} as const;

// Type filter options based on hook parameters
const typeFilterOptions: SelectOption[] = [
  { id: 0, name: 'All Types', value: '' },
  { id: 1, name: 'Create Card', value: 'CREATE' },
  { id: 2, name: 'Deposit', value: 'DEPOSIT' },
  { id: 3, name: 'Cancel Card', value: 'CANCEL' },
  { id: 4, name: 'Freeze Card', value: 'FREEZE' },
  { id: 5, name: 'Unfreeze Card', value: 'UNFREEZE' },
  { id: 6, name: 'Withdraw', value: 'WITHDRAW' },
];

const tableHead = ['Order No.', 'Amount', 'Type', 'Status', 'Transaction Time'];

// Helper function to format date
const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const CardTransactions = () => {
  const { t } = useTranslation();
  const { selectedCard } = useCardContext();
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [selectedTypeFilter, setSelectedTypeFilter] = useState<SelectOption>(
    typeFilterOptions[2],
  );
  const [orderNoFilter, setOrderNoFilter] = useState('');

  const {
    isLoading,
    records: transactionsData,
    total,
  } = useGetUserCardTxs({
    cardId: selectedCard?.id || 0,
    pageNum: currentPage,
    pageSize: pageSize,
    type: selectedTypeFilter.value
      ? (selectedTypeFilter.value as
          | 'CREATE'
          | 'DEPOSIT'
          | 'CANCEL'
          | 'FREEZE'
          | 'UNFREEZE'
          | 'WITHDRAW')
      : undefined,
    orderNo: orderNoFilter || undefined,
  });
  // Don't render if no card is selected
  if (!selectedCard?.id) {
    return (
      <div className='card'>
        <div className='flex items-center max-md:h-12'>
          <div className='pl-5 text-h6 max-lg:pl-3 max-md:mr-auto'>
            {t('cards.card_transactions')}
          </div>
        </div>
        <NoFound
          title={t('cards.no_card_selected')}
          sub={t('cards.select_card_to_view')}
        />
      </div>
    );
  }

  // Filter transactions based on search (client-side) since type and orderNo are now server-side
  const filteredTransactions = (transactionsData || []).filter(
    (transaction: any) => {
      const matchesSearch =
        search === '' ||
        String(transaction.id || transaction.cardTxId || '')
          .toLowerCase()
          .includes(search.toLowerCase()) ||
        String(transaction.status || '')
          .toLowerCase()
          .includes(search.toLowerCase()) ||
        String(transaction.note || transaction.description || '')
          .toLowerCase()
          .includes(search.toLowerCase()) ||
        String(transaction.merchantName || '')
          .toLowerCase()
          .includes(search.toLowerCase()) ||
        CARD_TRANSACTION_TYPES[
          transaction.type as keyof typeof CARD_TRANSACTION_TYPES
        ]?.label
          .toLowerCase()
          .includes(search.toLowerCase());

      return matchesSearch;
    },
  );

  // Calculate pagination info
  const totalPages = Math.ceil((total || 0) / pageSize);
  const hasNextPage = currentPage < totalPages;
  const hasPrevPage = currentPage > 1;

  // Handle pagination
  const handleNextPage = () => {
    if (hasNextPage) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (hasPrevPage) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Reset all filters
  const handleResetFilters = () => {
    setSelectedTypeFilter(typeFilterOptions[2]); // Keep DEPOSIT as default
    setOrderNoFilter('');
    setSearch('');
    setCurrentPage(1);
  };

  return (
    <div>
      <div className='flex items-center px-5 py-3 max-lg:px-3'>
        <Search
          className='w-70 mr-auto max-lg:w-64 max-md:w-full'
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder='Search...'
          isGray
        />
      </div>
      {/* Additional Filters Section */}
      <div className='flex flex-wrap items-center gap-4 px-5 py-3 max-lg:px-3 border-b border-s-stroke2'>
        <div className='flex items-center gap-2'>
          <label className='text-sm font-medium text-t-secondary'>
            Order No:
          </label>
          <input
            type='text'
            value={orderNoFilter}
            onChange={(e) => setOrderNoFilter(e.target.value)}
            placeholder='Enter order number'
            className='w-48 h-10 px-3 border border-s-stroke2 rounded-lg text-sm bg-b-surface2 text-t-primary placeholder-t-secondary focus:outline-none focus:border-s-highlight transition-colors'
          />
        </div>
        <button
          onClick={handleResetFilters}
          className='px-4 py-2 text-sm font-medium text-t-secondary border border-s-stroke2 rounded-lg hover:bg-b-surface2 transition-colors'
        >
          Reset Filters
        </button>
      </div>
      <div className='px-5 pb-3 max-lg:px-3'>
        <p className='text-sm text-t-secondary'>
          Card deposit transactions - view all deposit activities for the
          selected card.
        </p>
      </div>

      {/* Fixed height container for content and pagination */}
      <div className='h-[600px] flex flex-col'>
        {isLoading ? (
          <div className='flex-1 flex justify-center items-center'>
            <Spinner />
          </div>
        ) : filteredTransactions.length === 0 ? (
          <div className='flex-1 flex items-center justify-center'>
            <NoFound title='No card transactions found' />
          </div>
        ) : (
          <>
            {/* Scrollable table area */}
            <div className='flex-1 overflow-y-auto p-1 pt-6 max-md:pt-3 max-lg:px-0'>
              <div className='overflow-x-auto w-full'>
                <div className='min-w-[800px]'>
                  <Table
                    cellsThead={tableHead.map((head) => (
                      <th
                        className={cn(
                          '!h-12.5 last:text-left whitespace-nowrap',
                        )}
                        key={head}
                      >
                        {head}
                      </th>
                    ))}
                    isMobileVisibleTHead
                  >
                    {filteredTransactions.map((transaction: any, index) => (
                      <TableRow
                        key={transaction.id || transaction.cardTxId || index}
                      >
                        {/* Order No. */}
                        <td className='font-mono text-sm whitespace-nowrap'>
                          <div className='flex items-center gap-2'>
                            <span>
                              {shortenAddress(
                                String(
                                  transaction.orderNo ||
                                    transaction.id ||
                                    transaction.cardTxId ||
                                    '',
                                ),
                              )}
                            </span>
                            <button
                              onClick={() =>
                                copyToClipboard(
                                  String(
                                    transaction.orderNo ||
                                      transaction.id ||
                                      transaction.cardTxId ||
                                      '',
                                  ),
                                )
                              }
                              className='p-1 hover:bg-gray-100 rounded transition-colors'
                              title='Copy Order Number'
                            >
                              <Icon
                                name='copy'
                                className='!size-4 fill-t-secondary hover:fill-t-primary'
                              />
                            </button>
                          </div>
                        </td>
                        {/* Amount */}
                        <td className='text-right text-sub-title-2 max-md:text-body-2 max-md:font-normal whitespace-nowrap'>
                          <CurrencyFormat
                            value={Number(
                              transaction?.amount ||
                                transaction?.totalAmount ||
                                0,
                            )}
                            currency={getCurrencySymbol(
                              'en-US',
                              transaction?.currency || 'USD',
                            )}
                          />
                        </td>
                        {/* Type */}
                        <td>
                          <div className='inline-flex items-center gap-1'>
                            <div
                              className={`label whitespace-nowrap ${
                                CARD_TRANSACTION_TYPES[
                                  transaction.type as keyof typeof CARD_TRANSACTION_TYPES
                                ]?.color || 'label-gray'
                              }`}
                            >
                              {CARD_TRANSACTION_TYPES[
                                transaction.type as keyof typeof CARD_TRANSACTION_TYPES
                              ]?.label || transaction.type}
                            </div>
                          </div>
                        </td>
                        {/* Status */}
                        <td>
                          <div
                            className={`label whitespace-nowrap capitalize ${
                              STATUS_COLORS[
                                transaction.status as keyof typeof STATUS_COLORS
                              ] || 'label-gray'
                            }`}
                          >
                            {String(transaction.status || '').toLowerCase()}
                          </div>
                        </td>
                        {/* Transaction Time */}
                        <td className='max-md:text-caption w-32 whitespace-nowrap'>
                          {formatDate(
                            transaction?.transactionTime ||
                              transaction?.createdAt ||
                              transaction?.created_at ||
                              '',
                          )}
                        </td>
                      </TableRow>
                    ))}
                  </Table>
                </div>
              </div>
            </div>

            {/* Fixed pagination at bottom */}
            <div className='flex-shrink-0 border-t border-s-stroke2 pt-4 pb-2'>
              <div className='flex items-center justify-between px-6 max-lg:flex-col max-lg:gap-4'>
                <div className='text-sm text-t-secondary'>
                  Showing page {currentPage} of {totalPages} ({total} total
                  records)
                </div>
                <div className='flex items-center gap-2'>
                  <button
                    onClick={handlePrevPage}
                    disabled={!hasPrevPage}
                    className='flex items-center justify-center w-10 h-10 rounded-lg border border-s-stroke2 text-sm font-medium transition-colors hover:bg-b-surface2 disabled:opacity-50 disabled:cursor-not-allowed'
                  >
                    <Icon
                      name='arrow'
                      className='rotate-180 fill-t-secondary'
                    />
                  </button>

                  {/* Page numbers */}
                  <div className='flex items-center gap-1'>
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }

                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`flex items-center justify-center w-10 h-10 rounded-lg text-sm font-medium transition-colors ${
                            currentPage === pageNum
                              ? 'bg-b-primary text-t-primary border border-s-focus'
                              : 'border border-s-stroke2 hover:bg-b-surface2'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>

                  <button
                    onClick={handleNextPage}
                    disabled={!hasNextPage}
                    className='flex items-center justify-center w-10 h-10 rounded-lg border border-s-stroke2 text-sm font-medium transition-colors hover:bg-b-surface2 disabled:opacity-50 disabled:cursor-not-allowed'
                  >
                    <Icon name='arrow' className='fill-t-secondary' />
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CardTransactions;
